import {
  FC,
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowTopRightOnSquareIcon,
  ExclamationCircleIcon,
  MapIcon,
  MapPinIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { usePostHog } from "@posthog/react";
import type { Itinerary } from "$/models/Itinerary.model.ts";
import { hasMeaningfulText } from "$/utils/itinerary/hasMeaningfulText.ts";
import {
  getAccommodationForDay,
  getAccommodationStays,
} from "$/utils/itinerary/accommodationSchedule.ts";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import TripIsGenerated from "@/components/ui/TripIsGenerated/TripIsGenerated.tsx";
import LoaderOverlay from "@/components/ui/LoaderOverlay/LoaderOverlay.tsx";
import LoaderWrapper from "@/components/ui/LoaderWrapper/LoaderWrapper.tsx";
import TripGenerationFailed from "@/components/ui/TripGenerationFailed/TripGenerationFailed.tsx";
import TripHeader from "@/components/ui/TripHeader/TripHeader.tsx";
import RefinementLimitModal from "@/components/modals/RefinementLimitModal/RefinementLimitModal.tsx";
import CodyReplyModal from "@/components/modals/CodyReplyModal/CodyReplyModal.tsx";
import {
  CodyAsk,
  TripProvider,
  useTripContext,
} from "@/context/TripContext.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import { queryClient } from "@/main.tsx";
import requestPlacePhoto from "@/api/places/requestPlacePhoto.ts";
import type { PlacePhotoResult } from "$/dto/PlacePhotoResult.dto.ts";
import Routes from "@/router/Routes.ts";
import dayjs, { getNumberOfDays } from "$/utils/dayjs";
import { buildTripCalendar, type CalendarDay } from "@/utils/tripCalendar.ts";
import type {
  ActivityLocation,
  DayPlan,
  DayRow,
  MissingRow,
} from "$/dto/ItineraryData.dto.ts";
import mergeMissingRows from "@/utils/trip/mergeMissingRows.ts";
import missingRowMessage from "@/utils/trip/missingRowMessage.ts";
import currentCodySuggestions, {
  areCodySuggestionsPending,
} from "@/utils/trip/currentCodySuggestions.ts";
import {
  buildGoogleMapsUrl,
  getUniqueLocations,
  hasCoordinates,
  isUsableLocation,
} from "@/utils/googleMaps.ts";
import { capitalizeFirstLetter } from "@/utils/capitalizeFirstLetter.ts";
import { cn } from "@/utils/classNames.ts";
import { missingTranslationToReadable } from "@/utils/missingTranslationToReadable.ts";
import CodyPreset from "$/_const/trip/CodyPreset.ts";
import resolvePacePreset from "$/utils/refinements/resolvePacePreset.ts";
import CodyBar from "@/components/ui/CodyBar/CodyBar.tsx";
import TripActionsMenu from "@/components/functional/TripActionsMenu/TripActionsMenu.tsx";
import TripChangeSummary from "@/components/ui/TripChangeSummary/TripChangeSummary.tsx";
import CodyWorking from "@/components/ui/CodyWorking/CodyWorking.tsx";
import PlaceWidget from "@/components/ui/PlaceWidget/PlaceWidget.tsx";
import PlaceMapsLink from "@/components/ui/PlaceMapsLink/PlaceMapsLink.tsx";
import BookLinks from "@/components/functional/BookLinks/BookLinks.tsx";
import { isBookableRow, isBookableStay } from "@/utils/booking/isBookable.ts";
import DayChangeButtons from "@/components/ui/DayChangeButtons/DayChangeButtons.tsx";
import UnsplashCredit from "@/components/ui/UnsplashCredit/UnsplashCredit.tsx";
import buildPlaceMapsUrl from "@/utils/trip/placeMapsUrl.ts";

const DAY_GRID =
  "grid grid-cols-1 md:grid-cols-[7rem_minmax(0,1fr)] md:gap-x-8 xl:grid-cols-[8rem_minmax(0,44rem)] xl:gap-x-12";
const TIMELINE_ROW =
  "relative pb-10 last:pb-0 before:absolute before:-bottom-3 before:left-[3.5px] before:top-3 before:w-px before:bg-primary/25 before:transition-colors before:duration-500 last:before:hidden group-data-[active=true]:before:bg-primary/70 md:before:left-[calc(9rem+3.5px)] xl:before:left-[calc(11rem+3.5px)]";
const TIMELINE_TIME =
  "hidden text-sm font-medium leading-6 tabular-nums text-[#2F2D2A]/50 transition-colors duration-500 group-data-[active=true]:text-[#2F2D2A]/75 md:row-span-2 md:block xl:row-span-1";
const RUN_IN_ICON =
  "mr-1.5 inline-block h-4 w-4 -translate-y-px text-gold-deep";
const STOP_HIGHLIGHT =
  "isolate xl:after:absolute xl:after:-right-3 xl:after:-top-3 xl:after:bottom-7 xl:after:left-[calc(11rem+0.875rem)] xl:after:-z-10 xl:after:rounded-2xl xl:after:bg-primary/[0.09] xl:after:opacity-0 xl:after:transition-opacity xl:after:duration-300 xl:last:after:-bottom-3 xl:data-[active=true]:after:opacity-100";
const PANEL_LAYER =
  "self-start transition-[opacity,filter,visibility] duration-300 ease-out [grid-area:1/1] motion-reduce:transition-[opacity,visibility]";

const selectedUnsplashPhotos = new Map<string, Map<number, string>>();
const dayPhotoRequestQueues = new Map<string, Promise<void>>();

const queueDayPhotoRequest = async <Result,>(
  itineraryId: string,
  callback: () => Promise<Result>,
): Promise<Result> => {
  const previousRequest = dayPhotoRequestQueues.get(itineraryId) ??
    Promise.resolve();
  let finishRequest: () => void = () => {};
  const currentRequest = new Promise<void>((resolve) => {
    finishRequest = resolve;
  });

  dayPhotoRequestQueues.set(itineraryId, currentRequest);

  await previousRequest.catch(() => undefined);

  try {
    return await callback();
  } finally {
    finishRequest();

    if (dayPhotoRequestQueues.get(itineraryId) === currentRequest) {
      dayPhotoRequestQueues.delete(itineraryId);
    }
  }
};

type AccommodationSummary = {
  place: string;
  place_description?: string | null;
  check_in_day?: number;
  check_out_day?: number;
  booking_id?: string | null;
  geoapify_place_id?: string | null;
  google_place_id?: string | null;
  formatted_address?: string | null;
  city?: string | null;
  country_code?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  agoda_hotel_id?: number | null;
  agoda_city_id?: number | null;
};

const getDnaChipLabel = (value: string): string => {
  const compactValue = value
    .replace(/\s+/g, " ")
    .trim()
    .split(/\s+[—–-]\s+/)[0]
    .trim();

  const paceMatch = compactValue.match(/^pace\s*:\s*(.+)$/i);
  if (paceMatch) {
    const pace = paceMatch[1].trim();
    return capitalizeFirstLetter(
      /\bpace\b/i.test(pace) ? pace : `${pace} pace`,
    );
  }

  const avoidMatch = compactValue.match(/^avoid\s*:\s*(.+)$/i);
  if (avoidMatch) {
    return `No ${avoidMatch[1].trim().replace(/^no\s+/i, "")}`;
  }

  const label = compactValue
    .replace(
      /^(experiences?|dining|stay|location|food approach)\s*:\s*/i,
      "",
    )
    .replace(/,?\s+and\s+/i, " & ");

  return capitalizeFirstLetter(label);
};

const DayPhoto: FC<{
  day: number;
  itineraryId: string;
  title: string;
  activities: DayRow[];
  accommodationPlace?: string | null;
  destination?: string | null;
  tags?: string[];
  coverPhotoId?: string | null;
  frameClassName?: string;
}> = ({
  day,
  itineraryId,
  title,
  activities,
  accommodationPlace,
  destination,
  tags,
  coverPhotoId,
  frameClassName = "aspect-video xl:aspect-[3/2]",
}) => {
  const { t } = useTranslation();
  const locations = useMemo(() => getUniqueLocations(activities), [activities]);
  const photoLocations = useMemo(() => {
    if (locations.length < 2) return locations;

    if (day === 1 && accommodationPlace) {
      const hotelName = accommodationPlace.trim().toLowerCase();
      const nonHotelLocations = locations.filter((location) => {
        const locationName = location.name.trim().toLowerCase();

        return locationName !== hotelName &&
          !locationName.includes(hotelName) &&
          !hotelName.includes(locationName);
      });

      if (nonHotelLocations.length > 0) {
        return [...nonHotelLocations].reverse();
      }
    }

    const offset = (day - 1) % locations.length;
    return [...locations.slice(offset), ...locations.slice(0, offset)];
  }, [accommodationPlace, day, locations]);
  const photoIndex = day === 1 ? 1 : Math.max(day - 1, 0);

  const { data: photo, isLoading } = useQuery({
    queryKey: [
      "day-place-photo",
      itineraryId,
      day,
      photoLocations,
      photoIndex,
      destination,
      title,
      tags,
      coverPhotoId,
    ],
    enabled: photoLocations.length > 0,
    staleTime: 1000 * 60 * 20,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<PlacePhotoResult> =>
      queueDayPhotoRequest(itineraryId, async () => {
        const previousSelections = selectedUnsplashPhotos.get(itineraryId) ??
          new Map<number, string>();
        const excludedPhotoIds = [
          ...new Set([
            ...(coverPhotoId ? [coverPhotoId] : []),
            ...Array.from(previousSelections.entries())
              .filter(([selectedDay]) => selectedDay !== day)
              .map(([, photoId]) => photoId),
          ]),
        ];
        const data = await requestPlacePhoto({
          itinerary_id: itineraryId,
          locations: photoLocations,
          photo_index: photoIndex,
          unsplash_photo_index: Math.max(day - 1, 0),
          destination,
          day_title: title,
          day_tags: tags ?? [],
          excluded_photo_ids: excludedPhotoIds,
        });

        if (data.provider === "unsplash" && data.provider_photo_id) {
          previousSelections.set(day, data.provider_photo_id);
          selectedUnsplashPhotos.set(itineraryId, previousSelections);
        }

        return data;
      }),
  });

  if (isLoading) {
    return (
      <div
        className={cn(
          "w-full animate-pulse rounded-2xl bg-primary/20",
          frameClassName,
        )}
        aria-label={t("tripPage.loading_day_photo")}
      />
    );
  }

  if (!photo) {
    return (
      <div
        className={cn(
          "flex w-full items-center justify-center rounded-2xl bg-primary/15 px-8 text-center",
          frameClassName,
        )}
      >
        <span className="font-feature text-xl font-light italic text-[#2F2D2A]">
          {t("tripPage.day_number", { number: day })}: {title}
        </span>
      </div>
    );
  }

  return (
    <figure className="flex flex-col gap-2">
      <img
        className={cn("w-full rounded-2xl object-cover", frameClassName)}
        src={photo.photo_uri}
        alt={photo.place_name || title}
        loading="lazy"
      />
      {photo.attribution
        ? (
          <UnsplashCredit
            photographerName={photo.attribution.display_name}
            photographerUrl={photo.attribution.uri}
            sourceUrl={photo.source_url}
            className="text-text-secondary"
          />
        )
        : null}
    </figure>
  );
};

const DayDetail: FC<{
  activity: DayRow;
  day: number;
  stopIndex: number;
  isActive: boolean;
}> = ({ activity, day, stopIndex, isActive }) => {
  const { t } = useTranslation();
  const { trip, itineraryChanges } = useTripContext();
  const changeStatus = itineraryChanges?.activityStatuses.get(activity);
  const timeOfDay = t(
    `enum_translations.PartOfTheDay.${activity.part_of_the_day}`,
    capitalizeFirstLetter(
      missingTranslationToReadable(activity.part_of_the_day),
    ),
  );

  const isDepartureBooking = activity.booking_id?.endsWith("::departure") ??
    false;

  const sourceBookingId = isDepartureBooking
    ? activity.booking_id?.slice(0, -"::departure".length)
    : activity.booking_id;

  const booking = sourceBookingId
    ? trip?.data.what_has_been_booked?.find((row, index) =>
      (row.id ?? `booking-${index + 1}`) === sourceBookingId
    )
    : undefined;

  const exactMoment = isDepartureBooking ? booking?.end : booking?.dateTime;

  const hasExactStartTime = Boolean(
    exactMoment && /[T ]\d{2}:\d{2}/.test(exactMoment),
  );

  const hasSameDayExactEndTime = Boolean(
    !isDepartureBooking &&
      booking?.bookingType !== "flight" &&
      booking?.dateTime &&
      booking.end &&
      booking.dateTime.slice(0, 10) === booking.end.slice(0, 10) &&
      /[T ]\d{2}:\d{2}/.test(booking.end),
  );

  const exactStartTime = hasExactStartTime && exactMoment
    ? dayjs(exactMoment).format("HH:mm")
    : null;

  const exactEndTime = hasSameDayExactEndTime && booking?.end
    ? dayjs(booking.end).format("HH:mm")
    : null;

  const displayedTime = exactStartTime
    ? `${exactStartTime}${exactEndTime ? `–${exactEndTime}` : ""}`
    : timeOfDay;

  const [isWidgetUnavailable, setIsWidgetUnavailable] = useState(false);
  const markWidgetUnavailable = useCallback(
    () => setIsWidgetUnavailable(true),
    [],
  );
  const placeId = activity.location?.google_place_id?.trim() || null;
  const showWidget = Boolean(placeId) && !isWidgetUnavailable;
  const mapsUrl = useMemo(
    () => buildPlaceMapsUrl(activity.location),
    [activity.location],
  );

  return (
    <li
      data-stop={stopIndex}
      data-active={isActive}
      className={cn(DAY_GRID, TIMELINE_ROW, STOP_HIGHLIGHT)}
    >
      <p className={TIMELINE_TIME}>{displayedTime}</p>
      <div className="relative min-w-0 pl-7">
        <span
          aria-hidden="true"
          className={cn(
            "absolute left-0 top-2 z-10 h-2 w-2 rounded-full opacity-50 ring-4 ring-journi-theme-light transition-opacity duration-500 group-data-[active=true]:opacity-100",
            booking ? "bg-gold-deep" : "bg-primary",
          )}
        />
        <h3 className="font-sans text-base font-medium leading-6 text-[#2F2D2A] md:text-[1.0625rem]">
          <span className="font-normal text-[#2F2D2A]/65 md:hidden">
            {displayedTime}
            {" · "}
          </span>
          {activity.activity_title}
        </h3>
        {booking || changeStatus
          ? (
            <p className="mt-2 flex flex-wrap items-center gap-2">
              {changeStatus
                ? (
                  <span className="inline-flex h-5 items-center rounded-full bg-[#2F2D2A] px-2 text-[0.625rem] font-medium uppercase tracking-[0.12em] text-journi-theme-light">
                    {t(`tripPage.changes.badge.${changeStatus}`)}
                  </span>
                )
                : null}
              {booking
                ? (
                  <span className="inline-flex h-5 items-center rounded-full border border-primary/60 bg-primary/15 px-2 text-[0.625rem] font-medium uppercase tracking-[0.12em] text-[#2F2D2A]">
                    {t("tripCreateForm.whatHasBeenBooked.badge")}
                  </span>
                )
                : null}
              {booking?.bookingRef
                ? (
                  <span className="text-xs text-text-secondary">
                    {t("tripCreateForm.whatHasBeenBooked.badgeReference", {
                      reference: booking.bookingRef,
                    })}
                  </span>
                )
                : null}
            </p>
          )
          : null}
        {!showWidget && isUsableLocation(activity.location)
          ? (
            <p className="mt-2 flex items-start gap-1.5 text-sm text-[#3F3D3A]">
              <MapPinIcon
                className="mt-0.5 h-4 w-4 shrink-0 text-gold-deep"
                aria-hidden="true"
              />
              <span>
                {activity.location.formatted_address?.startsWith(
                    activity.location.name,
                  )
                  ? activity.location.formatted_address
                  : [
                    activity.location.name,
                    activity.location.formatted_address,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
              </span>
            </p>
          )
          : null}
        {hasMeaningfulText(activity.activity_description)
          ? (
            <p className="mt-2 text-pretty font-feature text-lg font-light italic leading-snug text-[#3F3D3A] md:text-xl">
              {activity.activity_description}
            </p>
          )
          : null}
        {hasMeaningfulText(activity.cody_note)
          ? (
            <p className="mt-1.5 text-pretty font-feature text-lg font-light italic leading-snug text-[#3F3D3A] md:text-xl">
              {t("tripPage.cody_note", { note: activity.cody_note })}
            </p>
          )
          : null}
        {activity.recommendation
          ? (
            <p className="mt-3 text-pretty text-sm leading-relaxed text-text-secondary md:text-[0.9375rem]">
              <SparklesIcon className={RUN_IN_ICON} aria-hidden="true" />
              <span className="font-medium text-[#2F2D2A]">
                {t("tripPage.why_cody_chose_this")}
              </span>
              <span className="sr-only">:</span>
              <span aria-hidden="true">{" · "}</span>
              {activity.recommendation}
            </p>
          )
          : null}
        {hasMeaningfulText(activity.dietary_note)
          ? (
            <p className="mt-2 text-pretty text-sm leading-relaxed text-[#3F3D3A] md:text-[0.9375rem]">
              <ExclamationCircleIcon
                className={RUN_IN_ICON}
                aria-hidden="true"
              />
              <span className="font-medium text-[#2F2D2A]">
                {t("tripPage.dietary_note")}
              </span>
              <span className="sr-only">:</span>
              <span aria-hidden="true">{" · "}</span>
              {activity.dietary_note}
            </p>
          )
          : null}
        {showWidget && placeId
          ? (
            <div className="mt-4 xl:hidden">
              <PlaceWidget
                placeId={placeId}
                tone="light"
                onUnavailable={markWidgetUnavailable}
                className="w-full max-w-[22rem] md:w-full md:max-w-[22rem]"
              />
            </div>
          )
          : null}
        <div className="mt-1 flex flex-wrap items-center gap-x-5">
          {isBookableRow(activity)
            ? (
              <BookLinks
                item={{ kind: "activity", row: activity }}
                day={day}
              />
            )
            : null}
          <PlaceMapsLink
            url={mapsUrl}
            placeName={activity.location?.name || activity.activity_title}
          />
        </div>
      </div>
    </li>
  );
};

const MissingRowItem: FC<{
  missing: MissingRow;
  stopIndex: number;
  isActive: boolean;
}> = ({ missing, stopIndex, isActive }) => {
  const { t } = useTranslation();
  const timeOfDay = t(
    `enum_translations.PartOfTheDay.${missing.part_of_the_day}`,
    capitalizeFirstLetter(
      missingTranslationToReadable(missing.part_of_the_day),
    ),
  );
  const message = missingRowMessage(missing);

  return (
    <li
      data-stop={stopIndex}
      data-active={isActive}
      className={cn(DAY_GRID, TIMELINE_ROW, STOP_HIGHLIGHT)}
    >
      <p className={TIMELINE_TIME}>{timeOfDay}</p>
      <div className="relative min-w-0 pl-7">
        <span
          className="absolute left-0 top-2 z-10 h-2 w-2 rounded-full border-2 border-primary/50 bg-journi-theme-light ring-4 ring-journi-theme-light"
          aria-hidden="true"
        />
        <p className="text-base font-medium leading-6 text-text-secondary md:text-[1.0625rem]">
          <span className="font-normal md:hidden">
            {timeOfDay}
            {" · "}
          </span>
          {t(`tripPage.missing_row.item_type.${missing.item_type}`)}
        </p>
        <p className="mt-1.5 text-pretty text-sm text-text-secondary md:text-[0.9375rem]">
          {t(message.key, { request: message.request })}
        </p>
      </div>
    </li>
  );
};

const DetailBlock: FC<{
  label: string;
  children: ReactNode;
}> = ({ label, children }) => (
  <div className="mt-8 rounded-2xl bg-cream-2 px-5 py-4 md:px-6 md:py-5">
    <h4 className="font-sans text-sm font-medium text-[#2F2D2A]">{label}</h4>
    <div className="mt-1.5 text-pretty font-feature text-lg font-light italic leading-snug text-[#3F3D3A] md:text-xl">
      {children}
    </div>
  </div>
);

const StopCard: FC<{
  index: number;
  placeId: string;
  isShown: boolean;
  onUnavailable: (index: number) => void;
}> = ({ index, placeId, isShown, onUnavailable }) => {
  const markUnavailable = useCallback(
    () => onUnavailable(index),
    [index, onUnavailable],
  );

  return (
    <div
      aria-hidden={!isShown}
      className={cn(
        PANEL_LAYER,
        isShown
          ? "visible opacity-100 blur-0"
          : "invisible opacity-0 blur-[2px]",
      )}
    >
      <PlaceWidget
        placeId={placeId}
        tone="light"
        onUnavailable={markUnavailable}
        className="w-full max-w-none md:w-full"
      />
    </div>
  );
};

const DaySection: FC<{
  dayPlan: DayPlan;
  itineraryId: string;
  flightBookingIds: ReadonlySet<string>;
  accommodationPlace?: string | null;
  accommodationLocation?: ActivityLocation | null;
  destination?: string | null;
  calendarDay?: CalendarDay | null;
  coverPhotoId?: string | null;
}> = ({
  dayPlan,
  itineraryId,
  flightBookingIds,
  accommodationPlace,
  accommodationLocation,
  destination,
  calendarDay,
  coverPhotoId,
}) => {
  const { t } = useTranslation();
  const { itineraryChanges } = useTripContext();
  const isDayChanged = itineraryChanges?.changedDays.has(dayPlan.day) ?? false;
  const sectionRef = useRef<HTMLElement>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsActive(entry.isIntersecting),
      { rootMargin: "-50% 0px -50% 0px" },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);
  const mapsUrl = useMemo(
    () =>
      buildGoogleMapsUrl(
        dayPlan.activities,
        flightBookingIds,
        accommodationPlace,
        accommodationLocation,
        destination,
      ),
    [
      dayPlan.activities,
      flightBookingIds,
      accommodationPlace,
      accommodationLocation,
      destination,
    ],
  );
  const timeline = useMemo(
    () => mergeMissingRows(dayPlan.activities, dayPlan.missing_rows),
    [dayPlan.activities, dayPlan.missing_rows],
  );
  const stopCards = useMemo(
    () =>
      timeline.flatMap((entry, index) => {
        const placeId = entry.kind === "row"
          ? entry.row.location?.google_place_id?.trim()
          : undefined;

        return placeId ? [{ index, placeId }] : [];
      }),
    [timeline],
  );
  const [activeStop, setActiveStop] = useState<number | null>(null);
  const [unavailableStops, setUnavailableStops] = useState<
    ReadonlySet<number>
  >(new Set());
  const markStopCardUnavailable = useCallback(
    (index: number) =>
      setUnavailableStops((previous) => new Set(previous).add(index)),
    [],
  );
  const shownCard = activeStop !== null &&
      !unavailableStops.has(activeStop) &&
      stopCards.some((card) => card.index === activeStop)
    ? activeStop
    : null;

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const stop = (entry.target as HTMLElement).dataset.stop;

          setActiveStop(stop === "header" ? null : Number(stop));
        });
      },
      { rootMargin: "-50% 0px -50% 0px" },
    );

    section.querySelectorAll("[data-stop]").forEach((element) =>
      observer.observe(element)
    );

    return () => observer.disconnect();
  }, [timeline]);

  return (
    <section
      ref={sectionRef}
      data-day={dayPlan.day}
      data-active={isActive}
      className="group border-t border-primary/25 py-12 lg:py-16 xl:grid xl:grid-cols-[minmax(0,55rem)_minmax(22rem,1fr)] xl:gap-x-12"
    >
      <div className="min-w-0">
        <header data-stop="header" className={cn(DAY_GRID, "gap-y-5")}>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 md:col-start-1 md:row-span-2 md:row-start-1 md:block xl:row-span-1">
            <p className="font-feature text-3xl font-light italic leading-none text-[#2F2D2A]/60 transition-colors duration-500 group-data-[active=true]:text-[#2F2D2A] md:text-4xl">
              {t("tripPage.day_number", { number: dayPlan.day })}
            </p>
            {calendarDay
              ? (
                <p className="text-sm font-medium text-[#2F2D2A]/65 md:mt-3">
                  {t("tripPage.day_date", {
                    weekday: calendarDay.weekday,
                    date: dayjs.utc(calendarDay.date).format("D MMMM"),
                  })}
                </p>
              )
              : null}
            {isDayChanged
              ? (
                <span className="inline-flex h-5 items-center rounded-full bg-[#2F2D2A] px-2 text-[0.625rem] font-medium uppercase tracking-[0.12em] text-journi-theme-light md:mt-3">
                  {t("tripPage.changes.badge.day")}
                </span>
              )
              : null}
          </div>
          <div className="order-first md:order-none md:col-start-2 md:row-start-1 md:max-w-xl xl:hidden">
            <DayPhoto
              day={dayPlan.day}
              itineraryId={itineraryId}
              title={dayPlan.title}
              activities={dayPlan.activities}
              accommodationPlace={accommodationPlace}
              destination={destination}
              tags={dayPlan.tags}
              coverPhotoId={coverPhotoId}
            />
          </div>
          <div className="min-w-0 md:col-start-2 md:row-start-2 xl:row-start-1">
            <h2 className="text-balance font-feature text-3xl font-light leading-[1.1] text-[#2F2D2A] md:text-4xl">
              {dayPlan.title}
            </h2>
            {dayPlan.tags.length > 0
              ? (
                <p className="mt-3 text-sm text-text-secondary md:text-base">
                  {dayPlan.tags.map(capitalizeFirstLetter).join(" · ")}
                </p>
              )
              : null}
            <div className="mt-5">
              {mapsUrl
                ? (
                  <a
                    href={mapsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-10 items-center gap-2 rounded-full border border-primary/50 bg-white px-4 text-sm font-medium text-[#2F2D2A] transition-[transform,border-color] duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-[0.97] [@media(hover:hover)]:hover:border-primary"
                  >
                    <MapIcon
                      className="h-4 w-4 text-gold-deep"
                      aria-hidden="true"
                    />
                    {t("tripPage.open_in_google_maps", {
                      number: dayPlan.day,
                    })}
                    <ArrowTopRightOnSquareIcon
                      className="h-4 w-4 text-gold-deep"
                      aria-hidden="true"
                    />
                  </a>
                )
                : (
                  <button
                    type="button"
                    disabled
                    className="inline-flex min-h-10 items-center gap-2 rounded-full border border-primary/30 bg-white/60 px-4 text-sm font-medium text-[#2F2D2A]/50"
                  >
                    <MapIcon className="h-4 w-4" aria-hidden="true" />
                    {t("tripPage.open_in_google_maps", {
                      number: dayPlan.day,
                    })}
                  </button>
                )}
            </div>
          </div>
        </header>

        <ul className="mt-10 md:mt-12">
          {timeline.map((entry, index) =>
            entry.kind === "row"
              ? (
                <DayDetail
                  key={`${entry.row.activity_title}-${index}`}
                  activity={entry.row}
                  day={dayPlan.day}
                  stopIndex={index}
                  isActive={isActive && activeStop === index}
                />
              )
              : (
                <MissingRowItem
                  key={`missing-${entry.missing.part_of_the_day}-${entry.missing.item_type}-${index}`}
                  missing={entry.missing}
                  stopIndex={index}
                  isActive={isActive && activeStop === index}
                />
              )
          )}
        </ul>

        <div className={DAY_GRID}>
          <div className="min-w-0 pl-7 md:col-start-2">
            <DayChangeButtons day={dayPlan.day} />

            {hasMeaningfulText(dayPlan.local_secret)
              ? (
                <DetailBlock label={t("tripPage.local_secret")}>
                  <p>{dayPlan.local_secret}</p>
                </DetailBlock>
              )
              : null}
          </div>
        </div>
      </div>

      <aside className="hidden xl:block">
        <div className="sticky top-8 grid max-w-[30rem] opacity-70 saturate-[.8] transition-[opacity,filter] duration-500 group-data-[active=true]:opacity-100 group-data-[active=true]:saturate-100">
          <div
            aria-hidden={shownCard !== null}
            className={cn(
              PANEL_LAYER,
              shownCard === null
                ? "visible opacity-100 blur-0"
                : "invisible opacity-0 blur-[2px]",
            )}
          >
            <DayPhoto
              day={dayPlan.day}
              itineraryId={itineraryId}
              title={dayPlan.title}
              activities={dayPlan.activities}
              accommodationPlace={accommodationPlace}
              destination={destination}
              tags={dayPlan.tags}
              coverPhotoId={coverPhotoId}
              frameClassName="aspect-[4/5] max-h-[calc(100dvh-12rem)]"
            />
          </div>
          {stopCards.map((card) => (
            <StopCard
              key={`${card.index}-${card.placeId}`}
              index={card.index}
              placeId={card.placeId}
              isShown={shownCard === card.index}
              onUnavailable={markStopCardUnavailable}
            />
          ))}
        </div>
      </aside>
    </section>
  );
};

const GENERATION_EVENT_STORAGE_PREFIX = "journi:posthog_generation_event:";

// The localStorage guard de-dupes across remounts,
// reloads and multiple tabs watching the same trip within this browser; it
// cannot de-dupe across devices/browsers, and an event never fires at all if
// every tab is closed for the whole generation window — an analytics
// undercount, never a fabricated or duplicated event.
const reportGenerationOutcome = (
  posthog: ReturnType<typeof usePostHog>,
  tripId: string | undefined,
  itinerary: Pick<Itinerary, "failed_generation" | "updated_at">,
): void => {
  if (!posthog || !tripId) return;

  const storageKey = `${GENERATION_EVENT_STORAGE_PREFIX}${tripId}:${itinerary.updated_at}`;

  try {
    if (localStorage.getItem(storageKey)) return;
    localStorage.setItem(storageKey, "1");
  } catch {
    // Storage unavailable (private mode, etc.) — fall through and report
    // anyway rather than silently dropping the event.
  }

  if (itinerary.failed_generation) {
    posthog.capture("itinerary_generation_failed", {
      trip_id: tripId,
      failure_stage: "background_generation",
    });
  } else {
    posthog.capture("itinerary_generation_succeeded", { trip_id: tripId });
  }
};

const AccommodationCard: FC<{
  accommodation: AccommodationSummary;
  location: ActivityLocation | null;
  arrivalDate?: string | null;
  isChanged: boolean;
}> = ({ accommodation, location, arrivalDate, isChanged }) => {
  const { t } = useTranslation();
  const [isWidgetUnavailable, setIsWidgetUnavailable] = useState(false);
  const markWidgetUnavailable = useCallback(
    () => setIsWidgetUnavailable(true),
    [],
  );
  const placeId = accommodation.google_place_id?.trim() || null;
  const showWidget = Boolean(placeId) && !isWidgetUnavailable;
  const mapsUrl = useMemo(() => buildPlaceMapsUrl(location), [location]);

  return (
    <section
      className={cn(
        "mt-4 grid gap-5 rounded-[14px] border-2 border-primary bg-[#050505] p-4 text-white",
        showWidget && "md:grid-cols-[15rem_minmax(0,1fr)] md:items-start",
      )}
    >
      {showWidget && placeId
        ? (
          <PlaceWidget
            placeId={placeId}
            tone="dark"
            onUnavailable={markWidgetUnavailable}
          />
        )
        : null}
      <div className="px-1 py-2 md:px-3">
        <p className="flex flex-wrap items-center gap-2 font-sans text-sm uppercase text-white/75">
          {t("tripPage.accommodation")}
          {accommodation.check_in_day !== undefined
            ? (
              <span className="rounded-full border border-primary px-2 py-0.5 text-xs text-primary">
                {t(
                  accommodation.booking_id
                    ? "tripPage.stay_booked"
                    : "tripPage.stay_suggested",
                )}
              </span>
            )
            : null}
          {isChanged
            ? (
              <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-[#2F2D2A]">
                {t("tripPage.changes.badge.accommodation")}
              </span>
            )
            : null}
        </p>
        <h2 className="mt-1 font-feature text-3xl font-medium text-primary md:text-4xl">
          {accommodation.place}
        </h2>
        {accommodation.check_in_day !== undefined &&
            accommodation.check_out_day !== undefined
          ? (
            <p className="mt-2 text-sm text-white/90">
              {t("tripPage.stay_dates", {
                checkIn: arrivalDate
                  ? dayjs
                    .utc(arrivalDate)
                    .add(accommodation.check_in_day - 1, "day")
                    .format("D MMM YYYY")
                  : t("tripPage.stay_day", {
                    day: accommodation.check_in_day,
                  }),
                checkOut: arrivalDate
                  ? dayjs
                    .utc(arrivalDate)
                    .add(accommodation.check_out_day - 1, "day")
                    .format("D MMM YYYY")
                  : t("tripPage.stay_day", {
                    day: accommodation.check_out_day,
                  }),
              })}
              {" · "}
              {t("tripPage.nights", {
                count: accommodation.check_out_day - accommodation.check_in_day,
              })}
            </p>
          )
          : null}
        {accommodation.place_description
          ? accommodation.booking_id
            ? (
              <p className="mt-3 max-w-3xl font-sans text-sm leading-relaxed text-white/90 md:text-base">
                {accommodation.place_description}
              </p>
            )
            : (
              <div className="mt-3">
                <p className="text-sm font-medium text-primary">
                  {t("tripPage.why_cody_chose_this")}
                </p>
                <p className="mt-1 max-w-3xl font-sans text-sm leading-relaxed text-white/90 md:text-base">
                  {accommodation.place_description}
                </p>
              </div>
            )
          : null}
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
          {isBookableStay(accommodation)
            ? (
              <BookLinks
                item={{ kind: "hotel", stay: accommodation }}
                day={accommodation.check_in_day}
                tone="dark"
              />
            )
            : null}
          <PlaceMapsLink
            url={mapsUrl}
            placeName={accommodation.place}
            tone="dark"
          />
        </div>
      </div>
    </section>
  );
};

const TripPageInner: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const posthog = usePostHog();
  const {
    isGenerating,
    isLoadingAction,
    loadingDays,
    tripId,
    itinerary: itineraryModel,
    isLoadingItinerary,
    refetchItinerary,
    hasErrorGeneratingItinerary,
    hasErrorGeneratingItineraryImage,
    trip,
    hasPendingPlaces,
    refetchPlaces,
    dismissCodyRefinementLimit,
    isCodyRefinementLimitOpen,
    codyRefinementLabel,
    codyRefinementPhase,
    itineraryChanges,
    lastRefinementAsk,
    isChangeSummaryDismissed,
    dismissChangeSummary,
    handleScrollToDay,
    lastChange,
    handleUndoLastChange,
    isUndoing,
  } = useTripContext();
  const itinerary = useMemo(() => itineraryModel?.data, [itineraryModel]);
  const codySuggestions = useMemo(
    () => currentCodySuggestions(itineraryModel?.cody_suggestions ?? null),
    [itineraryModel],
  );

  const arrivalDate = trip?.data.arrival_date ?? itinerary?.arrival_date ??
    null;
  const departureDate = trip?.data.departure_date ??
    itinerary?.departure_date ?? null;
  const dayCount = arrivalDate && departureDate
    ? getNumberOfDays(arrivalDate, departureDate)
    : 3;
  const startDate = itineraryModel?.updated_at ?? null;
  const estimatedDurationSeconds = Math.min(
    Math.max(dayCount * 15, 120),
    180,
  );

  const estimatedEndDate = startDate
    ? dayjs
      .utc(startDate)
      .add(estimatedDurationSeconds, "second")
      .toISOString()
    : null;

  const dateRange = useMemo(() => {
    if (!arrivalDate || !departureDate) return null;

    const arrival = dayjs.utc(arrivalDate);
    const departure = dayjs.utc(departureDate);

    if (
      arrival.year() === departure.year() &&
      arrival.month() === departure.month()
    ) {
      return `${arrival.format("D")} – ${departure.format("D MMMM YYYY")}`;
    }

    if (arrival.year() === departure.year()) {
      return `${arrival.format("D MMMM")} – ${departure.format("D MMMM YYYY")}`;
    }

    return `${arrival.format("D MMMM YYYY")} – ${
      departure.format("D MMMM YYYY")
    }`;
  }, [arrivalDate, departureDate]);

  const numberOfNights = useMemo(() => {
    if (!arrivalDate || !departureDate) return null;

    return Math.max(
      0,
      dayjs.utc(departureDate).diff(dayjs.utc(arrivalDate), "day"),
    );
  }, [arrivalDate, departureDate]);

  // Deterministic, code-computed day-to-date/weekday mapping — never sourced
  // from LLM-generated content (dayPlan.title), so a generated title can
  // never cause the UI to mislabel a weekday.
  const tripCalendar = useMemo(() => {
    if (!arrivalDate || !itinerary?.per_day_plan.length) return null;

    return buildTripCalendar(arrivalDate, itinerary.per_day_plan.length);
  }, [arrivalDate, itinerary?.per_day_plan.length]);

  const tripOverview = useMemo(() => {
    if (!itinerary) return "";

    return (
      itinerary.why_this_trip_fits_you || itinerary.description
    )
      .replace(/\s+/g, " ")
      .trim();
  }, [itinerary]);

  const flightBookingIds = useMemo(
    () =>
      new Set(
        (trip?.data.what_has_been_booked ?? []).flatMap((booking, index) =>
          booking.bookingType === "flight"
            ? [booking.id ?? `booking-${index + 1}`]
            : []
        ),
      ),
    [trip?.data.what_has_been_booked],
  );

  const accommodations = useMemo<AccommodationSummary[]>(
    () => (itinerary ? getAccommodationStays(itinerary) : []),
    [itinerary],
  );

  // Only ever returns a location built entirely from the accommodation's
  // own verified fields. Never borrows another venue's coordinates to
  // resolve a hotel that has none of its own — a displayed name paired
  // with an unrelated venue's coordinates is exactly the "routes to the
  // wrong place" failure this guards against. A hotel with no verified
  // coordinates or address is simply not used as a routing anchor; it
  // still displays normally, it just does not appear in the day's map.
  const getAccommodationLocation = useCallback(
    (accommodation?: AccommodationSummary | null): ActivityLocation | null => {
      if (!accommodation?.place) return null;

      const canonicalLocation: ActivityLocation = {
        name: accommodation.place,
        city: accommodation.city ?? "",
        country_code: accommodation.country_code ?? "",
        geoapify_place_id: accommodation.geoapify_place_id,
        google_place_id: accommodation.google_place_id,
        formatted_address: accommodation.formatted_address,
        latitude: accommodation.latitude,
        longitude: accommodation.longitude,
      };

      // A Google-verified venue only ever persists a place id, never
      // coordinates or an address (see googleVenueValidation.ts) — it is
      // still routable by name/id, just without a coordinate-precision
      // anchor.
      return hasCoordinates(canonicalLocation) ||
          canonicalLocation.formatted_address?.trim() ||
          canonicalLocation.google_place_id?.trim()
        ? canonicalLocation
        : null;
    },
    [],
  );

  const accommodationLocations = useMemo<(ActivityLocation | null)[]>(
    () => accommodations.map(getAccommodationLocation),
    [accommodations, getAccommodationLocation],
  );

  const accommodationByDay = useMemo(() => {
    const byDay = new Map<
      number,
      { place: string | null; location: ActivityLocation | null }
    >();

    itinerary?.per_day_plan.forEach((dayPlan) => {
      const accommodation = getAccommodationForDay(itinerary, dayPlan.day);

      byDay.set(dayPlan.day, {
        place: accommodation?.place ?? null,
        location: getAccommodationLocation(accommodation),
      });
    });

    return byDay;
  }, [getAccommodationLocation, itinerary]);

  const staysByCheckInDay = useMemo(() => {
    const days = new Set(itinerary?.per_day_plan.map((dayPlan) => dayPlan.day));
    const byDay = new Map<number, number[]>();
    const unscheduled: number[] = [];

    accommodations.forEach((accommodation, index) => {
      if (index === 0) return;

      const checkInDay = accommodation.check_in_day;

      if (checkInDay === undefined || !days.has(checkInDay)) {
        unscheduled.push(index);

        return;
      }

      byDay.set(checkInDay, [...(byDay.get(checkInDay) ?? []), index]);
    });

    return { byDay, unscheduled };
  }, [accommodations, itinerary]);

  const codyAsks = useMemo<CodyAsk[]>(() => {
    const tailored = codySuggestions
      .filter((suggestion) => !!suggestion.label?.trim() && !!suggestion.id)
      .map((suggestion) => ({
        label: suggestion.label.trim(),
        suggestionId: suggestion.id,
      }));

    const pacePreset = resolvePacePreset(user?.dna?.data);

    const general = [
      {
        label: pacePreset === CodyPreset.MAKE_MORE_ACTIVE
          ? t("tripPage.make_more_active")
          : t("tripPage.make_more_relaxed"),
        preset: pacePreset,
      },
      {
        label: t("tripPage.add_a_surprise"),
        preset: CodyPreset.ADD_A_SURPRISE,
      },
    ];

    return [...tailored, ...general];
  }, [codySuggestions, t, user?.dna?.data]);

  const dnaChips = useMemo(() => {
    if (!itinerary) return [];

    const seen = new Set<string>();

    const pace = itinerary.pace?.trim();
    const paceChip = pace && !/\bpace\b/i.test(pace) ? `${pace} pace` : pace;

    return [paceChip, itinerary.style, ...itinerary.your_dna_match]
      .map((value) => value?.trim())
      .filter((value): value is string => !!value)
      .map(getDnaChipLabel)
      .filter((label) => {
        const key = label.toLowerCase();

        if (!label || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 6);
  }, [itinerary]);

  const hasSeenGenerationRef = useRef(false);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (itineraryModel?.is_generating) hasSeenGenerationRef.current = true;

    if (
      !isLoadingItinerary &&
      !hasErrorGeneratingItinerary &&
      (itineraryModel?.is_generating ||
        areCodySuggestionsPending(itineraryModel, Date.now()) ||
        (itineraryModel?.is_generating_image &&
          !hasErrorGeneratingItineraryImage) ||
        hasPendingPlaces)
    ) {
      interval = setInterval(async () => {
        const { data: itinerariesLocal } = await refetchItinerary();
        const { data: refreshedPlaces } = await refetchPlaces();
        const localItinerary = itinerariesLocal?.[0];
        const placesStillGenerating = refreshedPlaces?.some((place) =>
          place.is_generating
        ) ?? false;

        if (
          localItinerary &&
          !localItinerary.is_generating &&
          !localItinerary.is_generating_image &&
          !areCodySuggestionsPending(localItinerary, Date.now()) &&
          !placesStillGenerating
        ) {
          clearInterval(interval);

          if (hasSeenGenerationRef.current) {
            hasSeenGenerationRef.current = false;
            reportGenerationOutcome(posthog, tripId, localItinerary);
          }

          await queryClient.invalidateQueries({ queryKey: ["itineraries"] });
          await queryClient.invalidateQueries({ queryKey: ["places"] });
        }
      }, 4000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [
    hasErrorGeneratingItinerary,
    hasErrorGeneratingItineraryImage,
    hasPendingPlaces,
    isLoadingItinerary,
    itineraryModel,
    refetchItinerary,
    refetchPlaces,
    posthog,
    tripId,
  ]);

  const renderAccommodationCard = (index: number) => (
    <AccommodationCard
      key={`${accommodations[index].booking_id ?? "suggested"}-${
        accommodations[index].check_in_day ?? index
      }`}
      accommodation={accommodations[index]}
      location={accommodationLocations[index] ?? null}
      arrivalDate={arrivalDate}
      isChanged={itineraryChanges?.changedAccommodations.has(index) ?? false}
    />
  );

  if (!isLoadingItinerary && !itineraryModel) {
    return <Navigate to={Routes.DASHBOARD} />;
  }

  return (
    <LoaderWrapper isLoading={isLoadingItinerary} className="min-h-[100dvh]">
      <div className="min-h-[100dvh] bg-journi-theme-light pb-36 text-[#3F3D3A]">
        {isCodyRefinementLimitOpen
          ? <RefinementLimitModal isOpen onClose={dismissCodyRefinementLimit} />
          : null}
        <CodyReplyModal />

        <div className="relative">
          <TripLayout.OpenMenuButton
            color="primary"
            className="absolute left-5 top-5 z-30 md:left-8 md:top-8"
          />
          {!isGenerating && itinerary && !hasErrorGeneratingItinerary
            ? (
              <TripActionsMenu
                tripId={tripId}
                disabled={isLoadingAction}
                className="absolute right-5 top-5 z-30 md:right-8 md:top-8"
              />
            )
            : null}

          {isGenerating && startDate && estimatedEndDate
            ? (
              <div className="flex min-h-[100dvh] items-center justify-center px-5 py-28">
                <TripIsGenerated
                  startDate={startDate}
                  estimatedEndDate={estimatedEndDate}
                  destination={trip?.data.trip_destination}
                />
              </div>
            )
            : null}
          {hasErrorGeneratingItinerary
            ? <TripGenerationFailed tripId={tripId} />
            : null}

          {!isGenerating && itinerary && !hasErrorGeneratingItinerary
            ? (
              <TripHeader
                is_generating_image={!!itineraryModel?.is_generating_image}
                hasError={hasErrorGeneratingItineraryImage}
                image_src={itineraryModel?.image}
                image_alt={itineraryModel?.image_alt}
                image_photographer_name={itineraryModel
                  ?.image_photographer_name}
                image_photographer_url={itineraryModel?.image_photographer_url}
                image_source_url={itineraryModel?.image_source_url}
                name={itinerary.name}
                className="h-[320px] md:h-[clamp(320px,42dvh,440px)] lg:h-[clamp(320px,42dvh,440px)]"
                updated_at={itineraryModel!.updated_at}
              />
            )
            : null}
        </div>

        {!isGenerating && itinerary && !hasErrorGeneratingItinerary
          ? (
            <main className="relative w-full px-5 md:px-8 lg:px-10 2xl:px-14">
              {codyRefinementPhase === "refining"
                ? <CodyWorking ask={codyRefinementLabel} />
                : null}
              {itineraryChanges && !isChangeSummaryDismissed
                ? (
                  <TripChangeSummary
                    changes={itineraryChanges}
                    ask={lastRefinementAsk}
                    onJumpToDay={handleScrollToDay}
                    onDismiss={dismissChangeSummary}
                    onUndo={handleUndoLastChange}
                    canUndo={!!lastChange && !isLoadingAction}
                    isUndoing={isUndoing}
                    className="mt-4"
                  />
                )
                : null}

              <section className="grid gap-8 pb-8 pt-4 md:pb-10 md:pt-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start">
                <div>
                  {dateRange
                    ? (
                      <h2 className="font-feature text-3xl font-light text-[#2F2D2A] md:text-4xl">
                        {dateRange}
                      </h2>
                    )
                    : null}
                  <p className="mt-4 max-w-[52rem] text-pretty font-feature text-xl font-light italic leading-relaxed text-[#3F3D3A] md:text-2xl">
                    {numberOfNights !== null
                      ? (
                        <>
                          <span className="text-primary">
                            {t("tripPage.nights", { count: numberOfNights })}
                          </span>
                          {" · "}
                        </>
                      )
                      : null}
                    {itinerary.description}
                  </p>
                </div>
              </section>

              {accommodations.length > 0 ? renderAccommodationCard(0) : null}

              <section className="mt-4 rounded-2xl bg-cream-2 px-6 py-5 md:px-8 md:py-7">
                <h2 className="font-sans text-sm font-medium text-[#2F2D2A]">
                  {t("tripPage.why_this_trip_is_yours", {
                    preferred_name: user?.preferred_name ?? user?.name ?? "",
                  })}
                </h2>
                <p className="mt-2 max-w-3xl text-pretty font-feature text-xl font-light leading-snug text-[#3F3D3A] md:text-2xl">
                  {tripOverview}
                </p>
                {dnaChips.length > 0
                  ? (
                    <ul
                      className="mt-5 flex flex-wrap gap-2.5"
                      aria-label={t("tripPage.your_dna_match", {
                        defaultValue: "Your DNA match",
                      })}
                    >
                      {dnaChips.map((match) => (
                        <li
                          key={match}
                          className="rounded-full border border-primary/50 bg-white px-3 py-1.5 font-sans text-xs font-medium leading-none text-[#3F3D3A] md:text-sm"
                        >
                          {match}
                        </li>
                      ))}
                    </ul>
                  )
                  : null}
              </section>

              <div className="mt-12">
                {itinerary.per_day_plan.map((dayPlan) => (
                  <Fragment key={`${dayPlan.day}-${dayPlan.title}`}>
                    <LoaderOverlay
                      loaderClassName="rounded-[0.2rem]"
                      isLoading={loadingDays.includes(dayPlan.day)}
                    >
                      <DaySection
                        dayPlan={dayPlan}
                        itineraryId={itineraryModel!.id}
                        flightBookingIds={flightBookingIds}
                        accommodationPlace={accommodationByDay.get(dayPlan.day)
                          ?.place}
                        accommodationLocation={accommodationByDay.get(
                          dayPlan.day,
                        )?.location}
                        destination={trip?.data.trip_destination}
                        calendarDay={tripCalendar?.find((entry) =>
                          entry.day === dayPlan.day
                        ) ?? null}
                        coverPhotoId={itineraryModel?.image_provider ===
                            "unsplash"
                          ? itineraryModel.image_provider_id
                          : null}
                      />
                    </LoaderOverlay>
                    {(staysByCheckInDay.byDay.get(dayPlan.day) ?? []).map(
                      renderAccommodationCard,
                    )}
                  </Fragment>
                ))}
                {staysByCheckInDay.unscheduled.map(renderAccommodationCard)}
              </div>

              <section className="grid gap-4 border-t border-primary/25 py-12 md:grid-cols-2 md:gap-6 lg:py-16">
                <div className="rounded-2xl bg-cream-2 px-6 py-5 md:px-7 md:py-6">
                  <h2 className="font-sans text-sm font-medium text-[#2F2D2A]">
                    {t("tripPage.journi_tip", { defaultValue: "Journi tip" })}
                  </h2>
                  <p className="mt-2 text-pretty font-feature text-lg font-light italic leading-snug text-[#3F3D3A] md:text-xl">
                    {itinerary.tip}
                  </p>
                </div>
                <div className="rounded-2xl bg-cream-2 px-6 py-5 md:px-7 md:py-6">
                  <h2 className="font-sans text-sm font-medium text-[#2F2D2A]">
                    {t("tripPage.your_dna_match", {
                      defaultValue: "Your DNA match",
                    })}
                  </h2>
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {itinerary.your_dna_match.map((match) => (
                      <li
                        key={match}
                        className="flex items-start gap-2.5 font-feature text-lg font-light italic leading-snug text-[#3F3D3A] md:text-xl"
                      >
                        <SparklesIcon
                          className="mt-1 h-4 w-4 shrink-0 text-gold-deep"
                          aria-hidden="true"
                        />
                        <span>{match}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </section>
            </main>
          )
          : null}

        {!isGenerating && itinerary && !hasErrorGeneratingItinerary
          ? <CodyBar asks={codyAsks} />
          : null}
      </div>
    </LoaderWrapper>
  );
};

const TripPage: FC = () => {
  const { id: tripId } = useParams();

  return (
    <TripProvider tripId={tripId ?? ""}>
      <TripPageInner />
    </TripProvider>
  );
};

export default TripPage;
